import ObjectId from 'bson-objectid';
import { Button, Col, Input, Row, Sidepanel, Text } from 'components';
import { Api, formatText, theme, uploadMedia, getYtId } from 'lib';
import React, { useState } from 'react';
import ReactPlayer from 'react-player';
import { useDispatch } from 'react-redux';
import { DatePicker } from 'screens/Admin/components';
import { Media } from 'screens/Admin/components/PostPanel/components';

const CreateP4PReplayModal = ({ visible, closeModal, team, onSuccess }) => {
  const [caption, setCaption] = useState(''),
    [media, setMedia] = useState([]),
    [youtubeLink, setYoutubeLink] = useState(''),
    [youtubeId, setYoutubeId] = useState(null),
    [createdAt, setCreatedAt] = useState(''),
    [loading, setLoading] = useState(false),
    [uploading, setUploading] = useState(false),
    [errors, setErrors] = useState([]),
    dispatch = useDispatch();

  async function submit() {
    setLoading(true);
    setErrors([]);

    let currentYoutubeId = youtubeId;
    // Auto-validate Youtube link if present but not previewed
    if (!media.length && !currentYoutubeId && youtubeLink) {
      const id = getYtId(youtubeLink);
      if (id) {
        currentYoutubeId = id;
        setYoutubeId(id);
      } else {
        setLoading(false);
        return setErrors([{ input: 'youtube', err: 'Invalid Youtube Link' }]);
      }
    }

    const newErrs = [];
    if (!media?.length && !currentYoutubeId && !caption) {
      newErrs.push({
        input: 'media',
        err: 'At least one type of media (Video/Youtube) or caption text is required'
      });
    }

    if (newErrs.length) {
      setLoading(false);
      return setErrors(newErrs);
    }

    try {
      const formatted = formatText(caption);
      const tags = formatted.filter((x) => x.type === 'tag').map((x) => x.text),
        mentioned = formatted.filter((x) => x.type === 'mention').map((x) => x.text);

      let res;

      // Handle Video Upload special case
      if (media[0]?.file && media[0].file.type.includes('video')) {
        res = await handleVideoUpload(tags, mentioned);
      } else {
        // Handle Images replacement (though P4P Replay should rely on Video/Youtube)
        // Or Youtube Link
        const uploadedMedia = await Promise.all(media.map((x, i) => uploadMedia(x, i)));

        const data = {
          caption,
          metaData: null,
          tags,
          mentioned,
          media: uploadedMedia,
          owner: team._id,
          createdAt: createdAt ? new Date(createdAt) : new Date(),
          youtubeId: currentYoutubeId || '',
          ownerType: 'team',
          type: 'p4p_replay'
        };

        res = await Api.post('/admin/createPost', data);
      }

      setLoading(false);

      if (res) {
        dispatch({
          type: 'HANDLE_RES',
          payload: { res: 'P4P Replay post created.' }
        });
        if (onSuccess) onSuccess();
        closeModal();
        resetForm();
      }
    } catch (e) {
      setLoading(false);
      console.error(e);
    }
  }

  async function handleVideoUpload(tags, mentioned) {
    setUploading(true);
    let data = new FormData();
    const fileId = ObjectId().toString();

    data.append('owner', team._id);
    data.append('createdAt', createdAt ? new Date(createdAt) : new Date());
    data.append('ownerType', 'team');
    data.append('caption', caption);
    data.append('tags', JSON.stringify(tags));
    data.append('mentioned', JSON.stringify(mentioned));
    data.append('type', 'p4p_replay');

    const file = media[0].file;
    const fileType = file.type.split('/')[1];
    const fileName = `${fileId}-1.${fileType}`;

    data.append('fileName', fileName);
    data.append('video', file, fileName);

    const res = await Api.post('/admin/createPost', data);
    setUploading(false);
    return res;
  }

  function resetForm() {
    setCaption('');
    setMedia([]);
    setYoutubeLink('');
    setYoutubeId(null);
    setCreatedAt('');
    setErrors([]);
  }

  function handlePreview() {
    const id = getYtId(youtubeLink);
    if (id) {
      setYoutubeId(id);
      setErrors([]);
    } else {
      setYoutubeId(null);
      setErrors([{ input: 'youtube', err: 'Invalid Youtube Link' }]);
    }
  }

  return (
    <Sidepanel pad="24px 0" wid={600} visible={visible} closeModal={closeModal}>
      <Col pad="0 24px">
        <Text h3 marg="0 0 24px">
          Create P4P Replay
        </Text>

        <Col wid="100%">
          {/* Hide File Upload if Youtube Preview is Active */}
          {!youtubeId && <Media media={media} setMedia={setMedia} isProcessing={uploading} />}

          {/* Hide Youtube Input if File is Selected */}
          {!media?.length && (
            <Col marg="24px 0 0" wid="100%">
              <Text b2 med marg="0 0 8px">
                Youtube Link
              </Text>
              <Row start wid="100%" center>
                <Col wid="100%">
                  <Input
                    wid="100%"
                    placeholder="Paste Youtube link here..."
                    value={youtubeLink}
                    onChange={(e) => {
                      setYoutubeLink(e.target.value);
                      setYoutubeId(null);
                      if (errors.length) setErrors([]);
                    }}
                    error={errors.find((x) => x.input === 'youtube')?.err}
                  />
                </Col>
                <Button
                  text="Preview"
                  onClick={handlePreview}
                  disabled={!youtubeLink}
                  style={{ width: '100px', height: '42px' }}
                  bordered
                  marg="0 0 0 12px"
                />
              </Row>

              {youtubeId && (
                <Col marg="16px 0 0">
                  <ReactPlayer
                    url={`https://www.youtube.com/watch?v=${youtubeId}`}
                    style={{ maxWidth: '100%' }}
                    controls
                    width="100%"
                  />
                </Col>
              )}
            </Col>
          )}
        </Col>

        <Row marg="24px 0 0" pos="relative">
          <Input
            title="Caption"
            wid="100%"
            multiline
            placeholder="Caption..."
            ht="100px"
            emoji
            value={caption}
            onChange={({ target }) => setCaption(target.value)}
            error={errors.find((x) => x.input === 'media')?.err}
          />
        </Row>

        <Text marg="24px 0 4px" b2 med>
          Publish date
        </Text>

        <DatePicker selected={createdAt} handleSelect={setCreatedAt} title={!createdAt && 'Now'} />

        <Row pad="54px 0 24px">
          <Button big text="Cancel" onClick={closeModal} marg="0 24px 0 0" bordered />

          <Button
            big
            onClick={submit}
            disabled={loading || uploading}
            text={loading || uploading ? 'Creating...' : 'Create'}
            bg={theme.SECONDARY}
          />
        </Row>
      </Col>
    </Sidepanel>
  );
};

export default CreateP4PReplayModal;
